import { parseHash, encodePayload, decodePayload } from './utils/payload.js';
import { generateSecret } from './crypto.js';
import { CreateView } from './ui/CreateView.js';
import { PairingView } from './ui/PairingView.js';
import {
  registerServiceWorker,
  initInstallPrompt,
  isIOS,
  isStandalone,
  isNativeApp,
  showIOSInstallModal,
  promptInstall,
  canInstall
} from './utils/pwa.js';

/**
 * Main application class
 * Handles routing and view management
 */
class App {
  constructor() {
    this.root = document.getElementById('app');
    this.currentView = null;
    this.installCallback = null;

    // Initialize PWA features
    this.initPWA();

    // Listen for hash changes
    window.addEventListener('hashchange', () => this.route());

    // Initial route
    this.route();
  }

  /**
   * Initialize PWA features (service worker, install prompt)
   */
  async initPWA() {
    // Register service worker
    await registerServiceWorker();

    // Listen for install prompt availability
    initInstallPrompt(() => {
      // Notify current view that install is available
      if (this.installCallback) {
        this.installCallback();
      }
    });

    // Show iOS install modal after a delay (if not installed and not in native app)
    if (isIOS() && !isStandalone() && !isNativeApp()) {
      setTimeout(() => {
        showIOSInstallModal();
      }, 3000);
    }

    // Handle share target (when app receives shared content)
    this.handleShareTarget();
  }

  /**
   * Handle Web Share Target API - when someone shares a link to this app
   */
  handleShareTarget() {
    const hash = window.location.hash;
    if (hash.startsWith('#/share')) {
      const params = new URLSearchParams(hash.slice(8)); // Remove "#/share?"
      const sharedUrl = params.get('url') || params.get('text') || '';

      // Check if it's a Handshake pairing URL
      if (sharedUrl.includes('#/p/')) {
        const payloadMatch = sharedUrl.match(/#\/p\/(.+)$/);
        if (payloadMatch) {
          const payload = decodePayload(payloadMatch[1]);
          if (payload) {
            window.location.hash = `/p/${payloadMatch[1]}`;
            return;
          }
        }
      }

      // Not a valid share, go to create view
      window.location.hash = '/new';
    }
  }

  /**
   * Route based on current URL hash
   */
  route() {
    const state = parseHash();

    // Clean up previous view
    this.cleanup();

    switch (state.view) {
      case 'create':
        this.showCreate();
        break;
      case 'pairing':
        this.showPairing(state.secret, state.nameA, state.nameB);
        break;
      default:
        this.showCreate();
    }
  }

  /**
   * Clean up current view
   */
  cleanup() {
    if (this.currentView && typeof this.currentView.destroy === 'function') {
      this.currentView.destroy();
    }
    this.root.innerHTML = '';
    this.currentView = null;
  }

  /**
   * Show the create pairing form
   */
  showCreate() {
    const view = new CreateView({
      onSubmit: (nameA, nameB) => {
        const secret = generateSecret();
        const payload = encodePayload(secret, nameA, nameB);
        window.location.hash = `/p/${payload}`;
      },
    });
    this.currentView = view;
    this.root.appendChild(view.element);
  }

  /**
   * Show the pairing view
   * @param {string} secret - Base32 encoded TOTP secret
   * @param {string} nameA - First person's name
   * @param {string} nameB - Second person's name
   */
  showPairing(secret, nameA, nameB) {
    const view = new PairingView({ secret, nameA, nameB });
    this.currentView = view;
    this.root.appendChild(view.element);
  }
}

// Start the app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new App());
} else {
  new App();
}
